import { useCallback, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  unlinkIssueAction,
  upsertIssuePlacementsByIdAction,
} from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  deleteSheet,
  fetchIssuesSheetAssociation,
} from "@adsk/acc-issues-core/state/redux/ducks/sheets";
import { fetchDocumentsByUrns } from "@adsk/acc-issues-core/state/redux/ducks/documents";
import {
  has3DFilePlacement,
  hasFilePlacement,
  hasSheetPlacement,
} from "@adsk/acc-issues-core/utils/data/placement";
import {
  Issue,
  IssueAttributes,
  IssuePlacementType,
  MarkupCreationPayload,
} from "@adsk/acc-issues-types";
import { useMaybeShowWatchersNotification } from "@adsk/acc-issues-common/hooks/notifications/useMaybeShowWatchersNotification";
import { useGetUniqueWatchersCount } from "@adsk/acc-issues-common/hooks/data/useGetUniqueWatchersCount";
import { sanitizeIssueAttributeMap } from "../utils/data/issueFieldSanitization";
import {
  trackIssueBaseFieldEdit,
  UpdateOrigin,
} from "../utils/analytics/issueActionsTracking";
import { IssueSnapshotCreateTrigger } from "../utils/analytics/analyticsLib093IssuesEvents";
import { useIssueSnapshotFlow } from "../IssueDetailPanel/IssueDetailTabPanel/hooks/IssueSnapshot";
import { useUpdateIssue } from "./useUpdateIssue";
import type { IssueChangeFn } from "../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/typesForFields";

export interface UseIssueChangeHandlerParams {
  issue: Issue;
  onSuccess?: (
    issueChanges: Partial<IssueAttributes>,
    issueSubResources?: {
      placements?: MarkupCreationPayload | null;
    }
  ) => void;
  onFailure: () => void;
}

export function useIssueChangeHandler({
  issue,
  onSuccess: onSuccessfulChange,
  onFailure: onUpdateFailed,
}: UseIssueChangeHandlerParams): {
  handleChange: IssueChangeFn;
  errors: { [key: string]: string };
} {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { maybeShowWatchersNotification } = useMaybeShowWatchersNotification();
  const { getUniqueWatchersCount } = useGetUniqueWatchersCount();
  const { uploadSnapshotAsAThumbnail } = useIssueSnapshotFlow();
  const updateIssue = useUpdateIssue();

  const handleChange = useCallback(
    async (
      inputIssueChanges,
      { onSuccess = undefined, onFailure = onUpdateFailed } = {}
    ) => {
      /*
       * There is some type weirdness here. There is work that needs to be done
       * to clarify confusion between in IssueAttributes type and Issue type:
       * there is no need or purpose to have both of them.
       *
       * Additionally, the actual type we are receiving here from `inputIssueChanges`,
       * which is not typed in the function signature, is potentially likePartial<IssueFormAttributes>,
       * because the custom fields key is an object instead of a list...
       *
       * But also potentially like Partial<Issue>, because there are keys in Issue that
       * are not on IssueFormAttributes. So, refactoring and streamlining of these types is needed
       * for better type safety here!
       */
      const { placements, snapshotBlob, ...issueChanges } =
        sanitizeIssueAttributeMap(inputIssueChanges) as IssueAttributes & {
          snapshotBlob?: Blob;
        };

      if (issueChanges.watcherObjects) {
        maybeShowWatchersNotification({
          watchers: issueChanges.watcherObjects,
        });
      }

      const handleSuccess = ({
        issueChanges,
        issueSubResources,
        enableTracking = true,
      }: {
        issueChanges: Partial<IssueAttributes>;
        issueSubResources?: {
          placements?: MarkupCreationPayload | null;
        };
        enableTracking?: boolean;
      }) => {
        onSuccess?.();
        onSuccessfulChange?.(issueChanges, issueSubResources);

        if (enableTracking) {
          const watchersSubResources = issueChanges.watcherObjects
            ? {
                currentWatcherUsersCount: getUniqueWatchersCount({
                  watchers: issueChanges.watcherObjects,
                }),
                previousWatcherUsersCount: getUniqueWatchersCount({
                  watchers: issue.watcherObjects || [],
                }),
              }
            : {};
          const analyticsData = {
            ...issueSubResources,
            ...watchersSubResources,
          };
          trackIssueBaseFieldEdit({
            issue,
            issueChanges,
            analyticsData,
          });
        }
      };

      const unlinkPlacement = placements && isEmpty(placements);

      if (unlinkPlacement) {
        try {
          await dispatch(unlinkIssueAction({ issue }));
          /**
           * since we store Markups data about 2D placements in our store, we need to clear it.
           * Otherwise, the placement text will remain after unlinking the placement.
           */
          if (!has3DFilePlacement(issue)) {
            dispatch(deleteSheet({ issueId: issue.id }));
          }
          handleSuccess({ issueChanges: { ...issueChanges, placements } });
        } catch {
          onFailure();
        }
        return;
      }

      if (placements && !isEmpty(placements)) {
        setErrors(prev => {
          const { placements: placementsError, ...restErrors } = prev;
          return placementsError ? restErrors : prev;
        });

        try {
          await dispatch(
            upsertIssuePlacementsByIdAction({
              id: issue.id,
              placements,
              options: { skipThumbnailGeneration: !!snapshotBlob },
            })
          );

          if (snapshotBlob) {
            await uploadSnapshotAsAThumbnail({
              issue,
              snapshotBlob,
              // In 3D, markups are baked into the snapshot, so for any new snapshot we lose them
              hasMarkups: has3DFilePlacement({ placements })
                ? false
                : issue.snapshotHasMarkups ?? false,
              trigger: IssueSnapshotCreateTrigger.LINK_ISSUE,
            });
          }

          if (hasFilePlacement({ placements })) {
            const documentUrns = placements?.reduce((result, p) => {
              if (p.type === IssuePlacementType.FILE) {
                result.push(p.lineageUrn);
              }
              return result;
            }, [] as string[]);

            if (documentUrns?.length) {
              await dispatch(
                fetchDocumentsByUrns({
                  documentUrns,
                  useCache: false,
                })
              );
            }
          }

          if (hasSheetPlacement({ placements })) {
            await dispatch(
              fetchIssuesSheetAssociation({
                issues: [issue],
                useCache: false,
              })
            );
          }

          handleSuccess({ issueChanges: { placements } });
        } catch (e) {
          setErrors(prev => ({
            ...prev,
            placement: "failed to create a placement",
          }));
          onFailure?.();
        }
      }

      if (!isEmpty(issueChanges)) {
        await updateIssue({
          issueId: issue.id,
          issueChanges,
          analyticsData: {
            origin: UpdateOrigin.DETAIL_PANEL,
          },
          onSuccess: () => {
            // Prevent double notification when there are issueChanges and placement to update (like placement and locationId)
            if (placements) {
              return;
            }
            handleSuccess({ issueChanges, enableTracking: false });
          },
          onFailure,
        });
      }
    },
    [
      onUpdateFailed,
      maybeShowWatchersNotification,
      onSuccessfulChange,
      getUniqueWatchersCount,
      issue,
      dispatch,
      uploadSnapshotAsAThumbnail,
      updateIssue,
    ]
  );

  return { handleChange, errors };
}
